import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "../App";
import { PageLayout } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ShieldCheck, FileText, CreditCard, ArrowLeft, MapPin, Download, Award, Loader2 } from "lucide-react";

const StatusIcon = ({ on, label }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} title={label}>
    {on ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
    {label}
  </span>
);

const CohortRoster = () => {
  const { cohortId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [notesFor, setNotesFor] = useState(null); // { user_id, name } when open

  const reload = async () => {
    try {
      const res = await fetch(`${API}/admin/cohorts/${cohortId}/roster`, { credentials: "include" });
      if (res.ok) setData(await res.json());
      else toast.error("Failed to load roster");
    } catch { toast.error("Network error"); }
  };

  useEffect(() => {
    const load = async () => { await reload(); setLoading(false); };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId]);

  const bulkIssueCertificates = async () => {
    if (!data) return;
    const total = data.summary?.total || 0;
    if (total === 0) {
      toast.error("No enrolled students to certify");
      return;
    }
    const confirm = window.confirm(
      `Issue program completion certificates to all ${total} enrolled student${total === 1 ? "" : "s"} in this cohort?\n\n` +
      `Program: ${data.program.name}\n` +
      `This will:\n` +
      `• Issue a verifiable certificate to each\n` +
      `• Mark each as Alumni + Certified\n` +
      `• Award the Program Graduate badge\n\n` +
      `Students who already have a cert for this program will be skipped.`
    );
    if (!confirm) return;
    setIssuing(true);
    try {
      const res = await fetch(
        `${API}/admin/programs/${data.program.program_id}/cohorts/${cohortId}/issue-certificates`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ program_id: data.program.program_id, cohort_id: cohortId }),
        }
      );
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.detail || "Failed");
      toast.success(`Issued ${out.issued} certificate${out.issued === 1 ? "" : "s"} (${out.skipped} already had one)`);
      await reload();
    } catch (e) {
      toast.error(e.message || "Failed to bulk-issue");
    } finally {
      setIssuing(false);
    }
  };

  const downloadAttendance = async (userId, userName) => {
    const res = await fetch(`${API}/users/${userId}/attendance/pdf?cohort_id=${cohortId}`, { credentials: "include" });
    if (!res.ok) { toast.error("Failed to download"); return; }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-${userName.replace(/\s+/g, "_")}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) return <PageLayout title="Cohort Roster"><p className="text-slate-500">Loading…</p></PageLayout>;
  if (!data) return <PageLayout title="Cohort Roster"><p className="text-slate-500">Cohort not found.</p></PageLayout>;

  const { program, cohort, roster, summary } = data;

  return (
    <PageLayout
      title={`${program.name} — ${new Date(cohort.start_date).toLocaleDateString()}`}
      subtitle={`Cohort roster · ${cohort.instructors?.join(", ") || "TBD"} · ${cohort.duration_weeks || program.duration_weeks}-week program`}
    >
      <div className="space-y-5" data-testid="cohort-roster">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-[#1E3A8A] hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <Button
            onClick={bulkIssueCertificates}
            disabled={issuing || roster.length === 0}
            className="bg-[#FFD507] hover:bg-[#FCC100] text-slate-900 font-semibold"
            data-testid="bulk-issue-cert-btn"
          >
            {issuing ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Issuing…</>
            ) : (
              <><Award className="w-4 h-4 mr-1.5" /> Issue Program Certificates ({summary.total})</>
            )}
          </Button>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="card-surface"><CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-500 uppercase">Total enrolled</p>
            <p className="text-2xl font-bold text-[#1E3A8A]">{summary.total}</p>
          </CardContent></Card>
          <Card className="card-surface"><CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-500 uppercase">Docs complete</p>
            <p className="text-2xl font-bold text-emerald-600">{summary.eligibility_complete}/{summary.total}</p>
          </CardContent></Card>
          <Card className="card-surface"><CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-500 uppercase">Fee paid</p>
            <p className="text-2xl font-bold text-emerald-600">{summary.fee_paid}/{summary.total}</p>
          </CardContent></Card>
          <Card className="card-surface"><CardContent className="pt-4 pb-4">
            <p className="text-xs text-slate-500 uppercase">Certified</p>
            <p className="text-2xl font-bold text-[#FFD507]">{summary.certified}/{summary.total}</p>
          </CardContent></Card>
        </div>

        {cohort.notes && (
          <Card className="card-surface border-l-4 border-[#FFD507]"><CardContent className="pt-3 pb-3">
            <p className="text-sm text-slate-700"><strong>Cohort note:</strong> {cohort.notes}</p>
          </CardContent></Card>
        )}

        <Card className="card-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-600">
                <th className="text-left p-3">Participant</th>
                <th className="text-left p-3">Location</th>
                <th className="text-left p-3">Eligibility Docs</th>
                <th className="text-left p-3">Reservation Fee</th>
                <th className="text-left p-3">Certified</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {roster.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-slate-400">No enrollments in this cohort yet.</td></tr>
              ) : roster.map((r) => (
                <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`roster-row-${r.user_id}`}>
                  <td className="p-3">
                    <p className="font-semibold text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-500">{r.email}</p>
                  </td>
                  <td className="p-3 text-slate-600">
                    {r.zipcode ? (
                      <a
                        href={`/admin/workforce-directory?zip=${encodeURIComponent(r.zipcode)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#1E3A8A] hover:underline"
                        data-testid={`workforce-${r.user_id}`}
                      >
                        <MapPin className="w-3.5 h-3.5" /> {r.zipcode}
                      </a>
                    ) : <span className="text-slate-400">—</span>}
                    {(r.city || r.state) && <p className="text-xs text-slate-500">{[r.city, r.state].filter(Boolean).join(", ")}</p>}
                  </td>
                  <td className="p-3">
                    <StatusIcon on={r.eligibility_complete} label={r.eligibility_complete ? "Complete" : `Missing ${r.documents_missing.length}`} />
                    {!r.eligibility_complete && (
                      <p className="text-[11px] text-slate-400 mt-1">{r.documents_missing.join(", ")}</p>
                    )}
                  </td>
                  <td className="p-3">
                    <StatusIcon on={r.fee_paid} label={r.fee_paid ? "Paid" : r.fee_status} />
                  </td>
                  <td className="p-3">
                    <StatusIcon on={r.is_certified} label={r.is_certified ? "Certified" : "Not yet"} />
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <Button size="sm" variant="outline" className="flex items-center gap-1.5" onClick={() => setNotesFor({ user_id: r.user_id, name: r.name })} data-testid={`open-notes-${r.user_id}`}>
                        <StickyNote className="w-3.5 h-3.5" /> Notes
                      </Button>
                      <Button size="sm" variant="outline" className="flex items-center gap-1.5" onClick={() => downloadAttendance(r.user_id, r.name)} data-testid={`download-attendance-${r.user_id}`}>
                        <Download className="w-3.5 h-3.5" /> Attendance PDF
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Notes Dialog */}
      <Dialog open={!!notesFor} onOpenChange={(open) => !open && setNotesFor(null)}>
        <DialogContent className="bg-white border-slate-200 max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <StickyNote className="w-4 h-4 text-brand-navy" />
              Case Notes — {notesFor?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">Internal admin/instructor notes about this participant. Never visible to the participant.</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-3">
            <div className="py-2">
              {notesFor && (
                <ParticipantNotesPanel participantId={notesFor.user_id} participantName={notesFor.name} />
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default CohortRoster;
